/**
 * A platform's (or app's) home page: H1 + one line, the big "What do you want to do?" search,
 * "Needs attention" (only when the app has a real status source; the section is left out
 * otherwise), "Common tasks" cards, and "Recent changes" from an audit the platform already keeps
 * (left out when there is none). Nothing here makes up a number: an item whose source could not
 * be read says so.
 */

import type { ReactNode } from 'react';
import { ShellIcon } from './icons';
import { PageHeader, StatusChip, type StatusTone } from './primitives';
import { SearchBox } from './SearchBox';
import type { SearchItem, ShellLinkComponent, ShellTask } from './types';

export interface AttentionItem {
  id: string;
  tone: StatusTone;
  /** Chip words: "Watching", "3 open", "All clear", "Could not check". */
  chip: string;
  title: string;
  text: string;
  path?: string;
  external?: boolean;
}

export interface RecentChange {
  id: string;
  what: string;
  why?: string | null;
  who: string;
  when: string;
}

export interface PlatformHomeProps {
  title: string;
  intro: ReactNode;
  Link: ShellLinkComponent;
  searchItems: ReadonlyArray<SearchItem>;
  onSearchPick: (item: SearchItem) => void;
  searchPlaceholder?: string;
  /** Null or undefined: no status source for this platform, the section is not shown. */
  attention?: { loading: boolean; items: ReadonlyArray<AttentionItem> } | null;
  tasks: ReadonlyArray<ShellTask>;
  /** Null or undefined: no audit to read, the section is not shown. */
  recent?: {
    loading: boolean;
    error?: string | null;
    items: ReadonlyArray<RecentChange>;
    seeAllPath?: string;
    seeAllLabel?: string;
    seeAllExternal?: boolean;
  } | null;
  /** Shown under the tasks (e.g. links that are not tasks). */
  footer?: ReactNode;
  attentionTour?: string;
  tasksTour?: string;
}

function TaskCard({ task, Link }: { task: ShellTask; Link: ShellLinkComponent }): JSX.Element {
  const body = (
    <>
      <span className="ps-task__icon" aria-hidden="true">
        <ShellIcon name={task.icon} size={22} />
      </span>
      <span className="ps-task__title">
        {task.title}
        {task.external && <ShellIcon name="external" size={14} />}
      </span>
      <span className="ps-task__blurb">{task.blurb}</span>
      <span className="ps-task__crumb">{task.crumb}</span>
    </>
  );
  if (task.external) {
    return (
      <a className="ps-task" href={task.path} target="_blank" rel="noopener noreferrer" data-tour={task.tour}>
        {body}
        <span className="ps-visually-hidden"> (opens in a new tab)</span>
      </a>
    );
  }
  return (
    <Link to={task.path} className="ps-task" data-tour={task.tour}>
      {body}
    </Link>
  );
}

function AttentionCard({ item, Link }: { item: AttentionItem; Link: ShellLinkComponent }): JSX.Element {
  const inner = (
    <>
      <StatusChip tone={item.tone}>{item.chip}</StatusChip>
      <span className="ps-attn__title">{item.title}</span>
      <span className="ps-attn__text">{item.text}</span>
    </>
  );
  if (!item.path) return <div className="ps-attn">{inner}</div>;
  if (item.external) {
    return (
      <a className="ps-attn ps-attn--link" href={item.path} target="_blank" rel="noopener noreferrer">
        {inner}
      </a>
    );
  }
  return (
    <Link to={item.path} className="ps-attn ps-attn--link">
      {inner}
    </Link>
  );
}

export function PlatformHome({
  title,
  intro,
  Link,
  searchItems: items,
  onSearchPick,
  searchPlaceholder,
  attention,
  tasks,
  recent,
  footer,
  attentionTour,
  tasksTour,
}: PlatformHomeProps): JSX.Element {
  return (
    <div className="ps-home">
      <PageHeader title={title} intro={intro} />

      <SearchBox
        variant="hero"
        items={items}
        onNavigate={onSearchPick}
        label="What do you want to do?"
        placeholder={searchPlaceholder ?? 'What do you want to do?'}
      />

      {attention && (
        <section aria-labelledby="ps-needs" className="ps-home__section" data-tour={attentionTour}>
          <h2 id="ps-needs" className="ps-home__h2">
            Needs attention
          </h2>
          {attention.loading && attention.items.length === 0 ? (
            <p className="ps-muted" role="status">
              Checking…
            </p>
          ) : (
            <div className="ps-attn-grid">
              {attention.items.map((it) => (
                <AttentionCard key={it.id} item={it} Link={Link} />
              ))}
            </div>
          )}
        </section>
      )}

      {tasks.length > 0 && (
        <section aria-labelledby="ps-tasks" className="ps-home__section">
          <h2 id="ps-tasks" className="ps-home__h2">
            Common tasks
          </h2>
          <div className="ps-task-grid" data-tour={tasksTour}>
            {tasks.map((t) => (
              <TaskCard key={t.id} task={t} Link={Link} />
            ))}
          </div>
        </section>
      )}

      {footer}

      {recent && (
        <section aria-labelledby="ps-recent" className="ps-recent">
          <div className="ps-recent__head">
            <h2 id="ps-recent" className="ps-home__h2">
              Recent changes
            </h2>
            {recent.seeAllPath &&
              (recent.seeAllExternal ? (
                <a href={recent.seeAllPath} target="_blank" rel="noopener noreferrer">
                  {recent.seeAllLabel ?? 'See the full audit'}
                </a>
              ) : (
                <Link to={recent.seeAllPath}>{recent.seeAllLabel ?? 'See the full audit'}</Link>
              ))}
          </div>
          {recent.loading && recent.items.length === 0 && (
            <p className="ps-muted" role="status">
              Loading the latest changes…
            </p>
          )}
          {recent.error && (
            <p className="ps-muted" role="status">
              Could not load recent changes: {recent.error}
            </p>
          )}
          {!recent.loading && !recent.error && recent.items.length === 0 && <p className="ps-muted">No changes recorded yet.</p>}
          {recent.items.length > 0 && (
            <ul className="ps-recent__list">
              {recent.items.map((c) => (
                <li key={c.id} className="ps-recent__item">
                  <span>
                    <strong>{c.what}</strong>
                    {c.why ? <> — &ldquo;{c.why}&rdquo;</> : null}
                  </span>
                  <span className="ps-recent__meta">
                    {c.who} · {c.when}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
